import { track } from './analytics/beacon';
import { fetchConfig, persistPreferences } from './api/client';
import { applySettings, resetDom } from './features/apply';
import {
    ensureSnapshots,
    overlayLocalPhrases,
    resetTranslationCache,
    restorePageText,
    translatePage,
} from './features/translate';
import { readPage, stopReading, warmupSpeech, isReading } from './features/tts';
import {
    bundledProfiles,
    mergeSettings,
    profileBySlug,
} from './profiles/defaults';
import {
    clearPreferences,
    getSessionId,
    loadPreferences,
    savePreferences,
} from './storage/local-preferences';
import {
    defaultSettings,
    normalizePosition,
    normalizeReadingRate,
    normalizeVoiceEngine,
    DEFAULT_COLOR,
    DEFAULT_READING_RATE,
    normalizeColor,
    type FeatureSettings,
    type InclusiveOptions,
    type InclusiveState,
    type Profile,
    type TranslationStatus,
    type VoiceEngine,
    type WidgetPosition,
    bundledLanguages,
} from './types';
import { mountWidget } from './widget/panel';

let state: InclusiveState | null = null;
let widget: ReturnType<typeof mountWidget> | null = null;
let profiles: Profile[] = bundledProfiles;
let translationGeneration = 0;
let translationAbort: AbortController | null = null;
let initToken = 0;
let initInFlight: Promise<void> | null = null;
const loadedFromOrigin = captureSdkOrigin();

export async function init(options: InclusiveOptions): Promise<void> {
    if (state && widget) {
        if (state.options.applicationId === options.applicationId) {
            return;
        }

        destroy();
    }

    if (initInFlight) {
        await initInFlight;

        if (state && widget) {
            if (state.options.applicationId === options.applicationId) {
                return;
            }

            destroy();
        }
    }

    const token = ++initToken;
    const run = runInit(options, token);
    initInFlight = run;

    try {
        await run;
    } finally {
        if (initInFlight === run) {
            initInFlight = null;
        }
    }
}

async function runInit(
    options: InclusiveOptions,
    token: number,
): Promise<void> {
    const stored = loadPreferences(options.applicationId);
    const sessionId = getSessionId(options.applicationId);
    const apiBaseUrl = resolveApiBaseUrl(options.apiBaseUrl);
    const position = resolvePosition(options.position);
    const color = resolveColor(
        options.color ?? options.colorCode ?? options.themeColor,
    );

    state = {
        options: {
            language: stored?.language ?? options.language ?? 'en',
            ...options,
            position,
            color,
            apiBaseUrl,
        },
        position,
        color,
        language: stored?.language ?? options.language ?? 'en',
        profile: stored?.profile ?? 'standard',
        settings: mergeSettings(stored?.settings ?? {}),
        open: false,
        reading: false,
        sessionId,
        languageServiceAvailable: false,
        azureTtsAvailable: false,
        googleTtsAvailable: false,
        languages: bundledLanguages,
        translationStatus: 'idle',
        voiceEngine: normalizeVoiceEngine(stored?.voiceEngine),
        browserVoiceURI:
            typeof stored?.browserVoiceURI === 'string'
                ? stored.browserVoiceURI
                : null,
        readingRate: normalizeReadingRate(
            stored?.readingRate ?? DEFAULT_READING_RATE,
        ),
    };

    applySettings(state.settings);
    warmupSpeech();

    const config = await fetchConfig(state.options);

    if (token !== initToken || state === null) {
        return;
    }

    const remoteProfiles = config?.profiles as
        | { data?: Profile[] }
        | Profile[]
        | undefined;
    const list = Array.isArray(remoteProfiles)
        ? remoteProfiles
        : Array.isArray(
                (remoteProfiles as { data?: Profile[] } | undefined)?.data,
            )
          ? (remoteProfiles as { data: Profile[] }).data
          : bundledProfiles;

    if (list.length > 0) {
        profiles = list.map((profile) => ({
            ...profile,
            settings: mergeSettings(profile.settings),
        }));
    }

    const features = config?.features as
        | { language?: boolean; azureTts?: boolean; googleTts?: boolean }
        | undefined;
    const remoteLanguages = config?.languages as
        | Array<{ code?: string; label?: string }>
        | undefined;
    state.languageServiceAvailable =
        config === null
            ? Boolean(state.options.apiBaseUrl)
            : Boolean(features?.language);
    state.azureTtsAvailable =
        config === null ? false : Boolean(features?.azureTts);
    state.googleTtsAvailable =
        config === null ? false : Boolean(features?.googleTts);
    if (Array.isArray(remoteLanguages) && remoteLanguages.length > 0) {
        state.languages = remoteLanguages
            .filter(
                (language): language is { code: string; label: string } =>
                    typeof language.code === 'string' &&
                    typeof language.label === 'string',
            )
            .map((language) => ({
                code: language.code,
                label: language.label,
            }));
    }

    if (token !== initToken || state === null) {
        return;
    }

    const current = state;

    const allowedProfile = profiles.find(
        (profile) => profile.slug === current.profile,
    );

    if (!allowedProfile) {
        const fallback = profiles[0];
        current.profile = fallback?.slug ?? 'standard';
        current.settings = mergeSettings(fallback?.settings ?? {});
        applySettings(current.settings);
    }

    if (
        !current.languages.some(
            (language) => language.code === current.language,
        )
    ) {
        current.language = current.languages[0]?.code ?? 'en';
    }

    if (current.voiceEngine === 'azure' && !current.azureTtsAvailable) {
        current.voiceEngine = 'auto';
    }

    if (current.voiceEngine === 'google' && !current.googleTtsAvailable) {
        current.voiceEngine = 'auto';
    }

    if (token !== initToken || state === null) {
        return;
    }

    widget?.destroy();
    widget = mountWidget(current, profiles, {
        toggle: (key) => {
            if (key === 'open') {
                open();

                return;
            }

            if (!state) {
                return;
            }

            const next = {
                ...state.settings,
                [key]: !state.settings[key as keyof FeatureSettings],
            };
            update({ settings: next, profile: 'custom' }, key);
        },
        setProfile,
        changeFont: (delta) => {
            if (!state) {
                return;
            }

            const fontSize = Math.min(
                4,
                Math.max(-2, state.settings.fontSize + delta),
            );
            update({ settings: { ...state.settings, fontSize } }, 'fontSize');
        },
        setLanguage: (language) => {
            void setLanguage(language);
        },
        setPosition: (pos) => {
            setPosition(pos);
        },
        reset,
        close,
        setVoiceEngine,
        setBrowserVoice,
        changeReadingRate,
        readPage: () => {
            if (!state) {
                return;
            }

            const wasReading = isReading();
            void readPage(state, {
                onChange: setReading,
            });

            if (!wasReading) {
                track(state, 'textToSpeech');
            }
        },
    });

    widget.render(state);

    if (state.language !== 'en' && state.language !== 'eng') {
        void syncPageLanguage();
    }
}

export function open(): void {
    if (!state || !widget) {
        return;
    }

    state = { ...state, open: true };
    widget.render(state);
    track(state, 'widgetOpened');
}

export function close(): void {
    if (!state || !widget) {
        return;
    }

    state = { ...state, open: false };
    widget.render(state);
}

export function reset(): void {
    if (!state) {
        return;
    }

    stopReading();
    restorePageText();
    clearPreferences(state.options.applicationId);
    update(
        {
            profile: 'standard',
            language: state.options.language ?? 'en',
            position: state.options.position ?? 'bottom-right',
            settings: defaultSettings(),
            translationStatus: 'idle',
            voiceEngine: 'auto',
            browserVoiceURI: null,
            readingRate: DEFAULT_READING_RATE,
        },
        'reset',
    );
}

export function setVoiceEngine(engine: VoiceEngine): void {
    if (!state) {
        return;
    }

    update({ voiceEngine: normalizeVoiceEngine(engine) }, 'voiceEngine');
}

export function setBrowserVoice(voiceURI: string | null): void {
    if (!state) {
        return;
    }

    update(
        { browserVoiceURI: voiceURI === '' ? null : voiceURI },
        'browserVoice',
    );
}

export function changeReadingRate(delta: number): void {
    if (!state) {
        return;
    }

    update(
        {
            readingRate: normalizeReadingRate(state.readingRate + delta),
        },
        'readingRate',
    );
}

export function setPosition(position: WidgetPosition): void {
    if (!state) {
        return;
    }

    const normalized = normalizePosition(position);
    update(
        {
            position: normalized,
            options: { ...state.options, position: normalized },
        },
        'position',
    );
}

export function setColor(color: string): void {
    if (!state) {
        return;
    }

    const normalized = normalizeColor(color);
    update(
        {
            color: normalized,
            options: { ...state.options, color: normalized },
        },
        'color',
    );
}

export function setProfile(slug: string): void {
    const profile = profileBySlug(slug, profiles) ?? profileBySlug(slug);

    if (!profile || !state) {
        return;
    }

    update(
        { profile: slug, settings: mergeSettings(profile.settings) },
        'profile',
        slug,
    );
}

export async function setLanguage(language: string): Promise<void> {
    if (
        state?.language === language &&
        state.translationStatus === 'translating'
    ) {
        return;
    }

    update({ language }, 'language');
    await syncPageLanguage();
}

export function getState(): InclusiveState | null {
    return state;
}

export function destroy(): void {
    initToken += 1;
    initInFlight = null;
    translationAbort?.abort();
    translationAbort = null;
    stopReading();
    restorePageText();
    resetTranslationCache();
    widget?.destroy();
    widget = null;
    state = null;
    resetDom();
}

function update(
    partial: Partial<InclusiveState>,
    feature: string,
    profile?: string,
): void {
    if (!state) {
        return;
    }

    state = {
        ...state,
        ...partial,
        settings: partial.settings ?? state.settings,
    };
    applySettings(state.settings);
    savePreferences(state);
    void persistPreferences(state);
    widget?.render(state);
    track(state, feature, profile);
}

async function syncPageLanguage(): Promise<void> {
    if (!state) {
        return;
    }

    translationAbort?.abort();
    const abort = new AbortController();
    translationAbort = abort;
    const generation = ++translationGeneration;

    if (state.language === 'en' || state.language === 'eng') {
        restorePageText();
        setTranslationStatus('idle');

        return;
    }

    setTranslationStatus('translating');

    let translated = false;

    if (state.languageServiceAvailable) {
        translated = await translatePage(
            state.options,
            state.language,
            abort.signal,
            () => {
                if (generation === translationGeneration && state) {
                    setTranslationStatus('translated');
                }
            },
        );
    } else {
        restorePageText();
        ensureSnapshots();
    }

    if (generation !== translationGeneration || !state) {
        return;
    }

    ensureSnapshots();
    translated = overlayLocalPhrases(state.language) || translated;

    if (generation !== translationGeneration || !state) {
        return;
    }

    if (translated) {
        setTranslationStatus('translated');

        return;
    }

    restorePageText();
    setTranslationStatus(
        state.languageServiceAvailable ? 'failed' : 'unavailable',
    );
}

function setReading(reading: boolean): void {
    if (!state) {
        return;
    }

    if (state.reading === reading) {
        return;
    }

    state = { ...state, reading };
    widget?.render(state);
}

function captureSdkOrigin(): string | undefined {
    const current = document.currentScript;

    if (current instanceof HTMLScriptElement && current.src !== '') {
        try {
            return new URL(current.src).origin;
        } catch {
            // The running script may not have a resolvable src.
        }
    }

    try {
        if (import.meta.url.startsWith('http')) {
            return new URL(import.meta.url).origin;
        }
    } catch {
        // import.meta.url is not a network URL in some test environments.
    }

    return undefined;
}

function resolveApiBaseUrl(explicit?: string): string | undefined {
    const trimmed = explicit?.trim();

    if (trimmed) {
        return trimmed.replace(/\/$/, '');
    }

    if (loadedFromOrigin) {
        return loadedFromOrigin;
    }

    const script = document.querySelector<HTMLScriptElement>(
        'script[src*="inclusive.js"]',
    );

    if (!script?.src) {
        return undefined;
    }

    try {
        return new URL(script.src).origin;
    } catch {
        return undefined;
    }
}

function resolvePosition(explicit?: WidgetPosition): WidgetPosition {
    if (explicit) {
        return normalizePosition(explicit);
    }

    if (typeof document !== 'undefined') {
        const script =
            (document.currentScript as HTMLScriptElement | null) ??
            document.querySelector<HTMLScriptElement>(
                'script[src*="inclusive.js"]',
            );

        const dataPosition = script?.dataset?.position;
        if (dataPosition) {
            return normalizePosition(dataPosition);
        }
    }

    return 'bottom-right';
}

function resolveColor(explicit?: string): string {
    if (explicit?.trim()) {
        return normalizeColor(explicit);
    }

    if (typeof document !== 'undefined') {
        const script =
            (document.currentScript as HTMLScriptElement | null) ??
            document.querySelector<HTMLScriptElement>(
                'script[src*="inclusive.js"]',
            );

        const dataColor =
            script?.dataset?.color ??
            script?.dataset?.colorCode ??
            script?.dataset?.themeColor ??
            script?.dataset?.accentColor;

        if (dataColor?.trim()) {
            return normalizeColor(dataColor);
        }
    }

    return DEFAULT_COLOR;
}

function setTranslationStatus(status: TranslationStatus): void {
    if (!state) {
        return;
    }

    state = { ...state, translationStatus: status };
    widget?.render(state);
}
